/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faCalendarXmark,
  faArrowRotateRight
} from '@fortawesome/free-solid-svg-icons';
import FeatherIcon from 'feather-icons-react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import { showAlert } from 'store/alert/alert.thunk';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import marriageGoodsService from 'service/marriageGoods';
import MarriageGoodsAddTable from './MarriageGoodsAddTable';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import UpdateMarriageGoodsListQuantity from './UpdateMarriageGoodsListQuantity';
import { pageToOffset } from 'utils/pageToOffset';
import UpdateReturnQuantity from './UpdateReturnQuantity';
import legalEntitySettingsService from 'service/legalEntitySettings';
import SupperModal from 'components/modals/SupperModal';
import { useAppContext } from 'providers/AppProvider';
import { useWindowWidth } from 'utils/useWindowSize';

export type MarriageGoodsType = {
  name: string;
  barcode: string;
  quantity: string;
  price_in: string;
  pid: number;
};

export const marriageGoodsBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'marriage_goods',
    active: true
  }
];

const MarriageGoods = () => {
  const { t } = useTranslation();

  const windowSize: any = useWindowWidth();

  const {
    config: { theme }
  } = useAppContext();

  const [openSearch, setOpenSearch] = useState(false);
  const [searchedValue, setSearchValue] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState<MarriageGoodsType>();
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openReturnUpdate, setOpenReturnUpdate] = useState(false);
  const [openSupperModal, setOpenSupperModal] = useState(false);
  const [confirmToDelete, setConfirmToDelete] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null
  );
  const [openSupperModalWriteOff, setOpenSupperModalWriteOff] = useState(false);
  const [confirmToWriteOff, setConfirmToWriteOff] = useState(false);
  const [selectedWriteOffData, setSelectedWriteOffdata] = useState<any | null>(
    null
  );

  const dispatch: Dispatch<any> = useDispatch();

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  const { data, refetch, isFetching, isLoading } = useQuery(
    ['GET_PRODUCT', searchedValue],
    async () => {
      const res = await marriageGoodsService
        .getList({
          limit: 10,
          offset: pageToOffset(currentPage, 10),
          search: searchedValue
        })
        .then((res: any) => {
          return res;
        });
      return res;
    },
    { enabled: !!currentPage || !searchedValue || searchedValue?.length > 2 }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const marriageGoodsData: MarriageGoodsType[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();
    return (
      data?.Defective_products?.map((items: any) => {
        return {
          barcode: items?.product?.barcode,
          name: items?.product?.name,
          quantity: items?.quantity,
          price_in: items?.product?.price_in
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.product?.price_in)
                .replace(/,/g, ' ')
            : '0',
          currency_pid: items?.product?.currency_pid || 0,
          nomenclature_pid: items?.product?.nomenclature_pid || 0,
          product_pid: items?.product_pid || 0,
          invoice: items?.invoice_number,
          measure_unit_pid: items?.product?.measure_unit_pid || 0,
          /* prettier-ignore */
          invoice_date:  formatDateToTimeZone(items?.invoice_date, timeZone),
          pid: items?.pid || 0
        };
      }) ?? []
    );
  }, [data]);

  const handleDelete = (pid: any) => {
    setSelectedInvoiceId(pid);
    setOpenSupperModal(true);
  };

  const handleWriteOff = (data: any) => {
    setSelectedWriteOffdata(data);
    setOpenSupperModalWriteOff(true);
  };

  const marriageGoodsTableColumns: ColumnDef<MarriageGoodsType>[] = [
    {
      id: 'action',
      cell: rowData => {
        console.log('rowData', rowData?.row?.original);

        return (
          <div className="d-flex justify-content-center gap-2">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip style={{ position: 'fixed' }}>Изменить</Tooltip>
              }
            >
              <div>
                <Button
                  variant="hover"
                  className="p-2"
                  onClick={() => {
                    setSelectedItems(rowData?.row?.original);
                    setOpenUpdate(true);
                  }}
                >
                  <FeatherIcon
                    icon="edit-2"
                    className="cursor-pointer"
                    size={20}
                  />
                </Button>
              </div>
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip style={{ position: 'fixed' }}>
                  {t('write_off')}
                </Tooltip>
              }
            >
              <div>
                <Button
                  variant="hover"
                  className="p-2"
                  onClick={() => handleWriteOff(rowData?.row?.original)}
                >
                  <FontAwesomeIcon icon={faCalendarXmark} className="fs-7" />
                </Button>
              </div>
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip style={{ position: 'fixed' }}>{t('return')}</Tooltip>
              }
            >
              <div>
                <Button
                  variant="hover"
                  className="p-2"
                  onClick={() => {
                    setOpenReturnUpdate(true);
                    setSelectedItems(rowData?.row?.original);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowRotateRight} className="fs-7" />
                </Button>
              </div>
            </OverlayTrigger>

            <Button
              variant="hover"
              className="p-2"
              onClick={() => handleDelete(rowData?.row?.original?.pid)}
            >
              <FeatherIcon
                icon="trash-2"
                className="cursor-pointer text-danger"
                size={20}
              />
            </Button>
          </div>
        );
      },

      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },
    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'quantity',
      header: t('quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price_in',
      header: t('purchase_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'invoice',
      header: t('invoice_number'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'invoice_date',
      header: t('date_invoice'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: marriageGoodsData,
    columns: marriageGoodsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const formatPrice = (price: any) => {
    const priceNumber = Number(price);
    if (isNaN(priceNumber)) {
      return { main: '0', decimals: '00' };
    }
    const priceParts = Number.isInteger(priceNumber)
      ? [priceNumber?.toString(), '00']
      : priceNumber?.toFixed(4).split('.');
    return {
      main: priceParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
      decimals: priceParts[1]
    };
  };

  useEffect(() => {
    if (selectedInvoiceId && confirmToDelete) {
      marriageGoodsService?.delete(selectedInvoiceId)?.finally(() => {
        refetch();
        setOpenSupperModal(false);
        setConfirmToDelete(false);
        setSelectedInvoiceId(null);
      });
    }
  }, [confirmToDelete, selectedInvoiceId, refetch]);

  useEffect(() => {
    if (selectedWriteOffData && confirmToWriteOff) {
      const priceIn =
        typeof selectedWriteOffData.price_in === 'string'
          ? selectedWriteOffData.price_in.split(' ').join('')
          : selectedWriteOffData.price_in.toString();

      const createData = {
        currency_pid: selectedWriteOffData?.currency_pid,
        nomenclature_pid: selectedWriteOffData?.nomenclature_pid,
        price_in: +priceIn || 0,
        product_pid: selectedWriteOffData?.product_pid,
        quantity: selectedWriteOffData?.quantity,
        quantity_actual: 0
        // status_pid: 'af0fe0e3-3357-4b29-a33f-138c89cb75db'
      };

      const updatedData = {
        pid: selectedWriteOffData?.pid,
        inventory_items: [createData]
      };
      marriageGoodsService?.createWriteOff(updatedData).then((res: any) => {
        if (res) {
          dispatch(
            showAlert({
              title: `${t('marriage_goods')} ${t('successfully_added')}`,
              type: 'success'
            })
          );
          refetch();
          setOpenSupperModalWriteOff(false);
          setConfirmToWriteOff(false);
          setSelectedWriteOffdata(null);
        }
      });
    }
  }, [confirmToWriteOff, selectedWriteOffData, refetch]);

  return (
    <div>
      <PageBreadcrumb items={marriageGoodsBreadcrumbItems} />

      <h2 className="mb-5">{t('marriage_goods')}</h2>

      <div className="d-flex gap-2 justify-content-between">
        <div className="form-icon-container">
          <Form.Floating>
            <Form.Control
              type="text"
              placeholder={t('enter_product')}
              onFocus={e => e.target.select()}
              onChange={e => setSearchValue(e.target.value)}
              className="form-icon-input"
            />
            <label
              htmlFor="floatingInputCustom"
              className="form-icon-label text-700"
            >
              {t('enter_product')}
            </label>
          </Form.Floating>
          <FontAwesomeIcon
            icon={faSearch}
            className="text-900 fs-9 form-icon"
          />
        </div>

        <div className="d-flex justify-content-between gap-2">
          <Button
            className="d-flex align-items-center gap-2"
            type="button"
            variant="primary"
            onClick={() => setOpenSearch(true)}
            style={{ height: '48px' }}
          >
            <FontAwesomeIcon icon={faPlus} />
            {windowSize > 768 && t('add')}
          </Button>
        </div>
      </div>

      <AdvanceTableProvider {...table}>
        <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            isLoading={loading}
          />
          <AdvanceTableCPagination
            count={pageCount}
            page={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </AdvanceTableProvider>

      <div
        className={
          theme === 'dark'
            ? 'dark-purchase-footer-info'
            : 'purchase-footer-info'
        }
      >
        <div className="box">
          <div className="d-flex gap-2 my-auto">
            <p className="mb-0" style={{ fontWeight: '800' }}>
              {t('total_quantity')}:
            </p>
            <span style={{ fontWeight: '800' }}>{data?.total_quantity}</span>
          </div>
        </div>
        <div className="box">
          <p className="mb-0" style={{ fontWeight: '800' }}>
            {t('total_amount_receipts')}
          </p>

          {data?.total_amount?.map((amount: any) => {
            const { main, decimals } = formatPrice(amount?.amount);
            return (
              <div
                key={amount.currency_id}
                className="d-flex justify-content-between gap-2"
              >
                <span>{amount?.currency.name.ru}:</span>
                <span style={{ fontWeight: '800' }}>
                  {main}.{decimals}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <SupperModal
        show={openSupperModal}
        setShow={setOpenSupperModal}
        setConfirm={setConfirmToDelete}
        title={t('marriage_goods')}
        bodyText={t('you_want_delete')}
      />

      <SupperModal
        show={openSupperModalWriteOff}
        setShow={setOpenSupperModalWriteOff}
        setConfirm={setConfirmToWriteOff}
        title={t('write_off')}
        bodyText={t('you_want_write_off')}
      />

      <Modal
        show={openSearch}
        onHide={() => {
          setOpenSearch(false);
          refetch();
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('search')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MarriageGoodsAddTable />
        </Modal.Body>
      </Modal>

      <Modal
        show={openUpdate}
        onHide={() => setOpenUpdate(false)}
        size="sm"
        centered
        contentClassName="border border-300"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('changed')} {t('quantity')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UpdateMarriageGoodsListQuantity
            refetch={refetch}
            setOpenUpdate={setOpenUpdate}
            selectedItems={selectedItems}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={openReturnUpdate}
        onHide={() => setOpenReturnUpdate(false)}
        size="sm"
        centered
        contentClassName="border border-300"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('return')} {t('quantity')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UpdateReturnQuantity
            refetch={refetch}
            setOpenReturnUpdate={setOpenReturnUpdate}
            selectedItems={selectedItems}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MarriageGoods;
